import type ApiClient from './api/apiClient';
import type { ApiResponse } from './api/apiClient';

export interface ApplicationTheme {
  themeID: number;
  themeName: string;
  siteID?: number | null;
  applicationType: 'Admin' | 'Online' | 'Both';
  isActive: boolean;

  // Core Colors
  primaryColor: string;
  secondaryColor: string;
  successColor: string;
  dangerColor: string;
  warningColor: string;
  infoColor: string;
  lightColor: string;
  darkColor: string;
  mutedColor: string;
  whiteColor: string;

  // Background Colors
  pageBackgroundColor: string;
  cardBackgroundColor: string;
  sidebarBackgroundColor: string;
  headerBackgroundColor: string;
  footerBackgroundColor: string;

  // Text Colors
  primaryTextColor: string;
  secondaryTextColor: string;
  linkTextColor: string;
  linkHoverTextColor: string;
  headingTextColor: string;

  // Form Colors
  inputBackgroundColor: string;
  inputBorderColor: string;
  inputFocusBorderColor: string;
  inputTextColor: string;
  inputPlaceholderColor: string;

  // Button Colors
  buttonPrimaryBackgroundColor: string;
  buttonPrimaryTextColor: string;
  buttonSecondaryBackgroundColor: string;
  buttonSecondaryTextColor: string;
  buttonHoverOpacity: string;
  buttonBorderRadius: string;

  // Table Colors
  tableHeaderBackgroundColor: string;
  tableHeaderTextColor: string;
  tableRowHoverColor: string;
  tableBorderColor: string;
  tableStripedRowColor: string;

  // Card Colors
  cardBorderColor: string;
  cardShadow: string;
  cardHeaderBackgroundColor: string;
  cardHeaderTextColor: string;

  // Navigation Colors
  navbarBackgroundColor: string;
  navbarTextColor: string;
  navbarActiveColor: string;
  navbarHoverColor: string;
  navbarBorderColor: string;

  // Syncfusion Theme
  syncfusionTheme: string;
  syncfusionAccentColor: string;
  syncfusionFontFamily: string;

  // Typography
  fontFamily: string;
  fontSizeBase: string;
  fontSizeH1: string;
  fontSizeH2: string;
  fontSizeH3: string;

  // Spacing
  spacingXS: string;
  spacingSM: string;
  spacingMD: string;
  spacingLG: string;
  spacingXL: string;

  // Custom CSS
  customCSS?: string | null;
}

export interface ActiveThemeResponse {
  theme: ApplicationTheme;
  css: string;
}

export interface IThemeService {
  getActiveTheme(applicationType?: string): Promise<ApiResponse<ActiveThemeResponse>>;
  getActiveThemeCSS(applicationType?: string): Promise<string>;
  getThemeById(id: number): Promise<ApiResponse<ApplicationTheme>>;
  getAllThemes(): Promise<ApiResponse<ApplicationTheme[]>>;
  getAllThemesAllSites(): Promise<ApiResponse<ApplicationTheme[]>>;
  getGlobalThemes(): Promise<ApiResponse<ApplicationTheme[]>>;
  createTheme(theme: Partial<ApplicationTheme>): Promise<ApiResponse<ApplicationTheme>>;
  createGlobalTheme(theme: Partial<ApplicationTheme>): Promise<ApiResponse<ApplicationTheme>>;
  updateTheme(id: number, theme: Partial<ApplicationTheme>): Promise<ApiResponse<ApplicationTheme>>;
  deleteTheme(id: number): Promise<ApiResponse<void>>;
  setActiveTheme(id: number, applicationType?: string): Promise<ApiResponse<void>>;
  previewTheme(id: number): Promise<string>;
}

export function createThemeService(apiClient: ApiClient): IThemeService {
  return {
    async getActiveTheme(applicationType: string = 'Both'): Promise<ApiResponse<ActiveThemeResponse>> {
      const params: any = { applicationType };

      // For public apps (FitTrackOnline), extract siteId and tenantId from URL
      // For authenticated apps (FitTrackReact), the API key in headers determines the tenant
      // Only extract from URL if we're in a browser and the URL matches the public app pattern
      if (typeof window !== 'undefined') {
        const pathMatch = window.location.pathname.match(/^\/([^/]+)\/(\d+)\//);
        if (pathMatch) {
          // This is FitTrackOnline with pattern: /tenantId/siteId/...
          params.tenantId = pathMatch[1];
          params.siteId = pathMatch[2];
        }
        // For FitTrackReact (authenticated), don't add these params
        // The backend will use the API key to determine tenant and siteId from user session
      }

      return apiClient.get<ActiveThemeResponse>('/api/Theme/active', { params });
    },

    async getActiveThemeCSS(applicationType: string = 'Both'): Promise<string> {
      // This would need to be implemented if CSS-only endpoint is needed
      // For now, use getActiveTheme and extract CSS from response
      const response = await this.getActiveTheme(applicationType);
      return response.data?.css || '';
    },

    async getThemeById(id: number): Promise<ApiResponse<ApplicationTheme>> {
      return apiClient.get<ApplicationTheme>(`/api/Theme/${id}`);
    },

    async getAllThemes(): Promise<ApiResponse<ApplicationTheme[]>> {
      return apiClient.get<ApplicationTheme[]>('/api/Theme');
    },

    async getAllThemesAllSites(): Promise<ApiResponse<ApplicationTheme[]>> {
      return apiClient.get<ApplicationTheme[]>('/api/Theme/all');
    },

    async getGlobalThemes(): Promise<ApiResponse<ApplicationTheme[]>> {
      return apiClient.get<ApplicationTheme[]>('/api/Theme/global');
    },

    async createTheme(theme: Partial<ApplicationTheme>): Promise<ApiResponse<ApplicationTheme>> {
      return apiClient.post<ApplicationTheme>('/api/Theme', theme);
    },

    async createGlobalTheme(theme: Partial<ApplicationTheme>): Promise<ApiResponse<ApplicationTheme>> {
      return apiClient.post<ApplicationTheme>('/api/Theme/global', theme);
    },

    async updateTheme(id: number, theme: Partial<ApplicationTheme>): Promise<ApiResponse<ApplicationTheme>> {
      return apiClient.put<ApplicationTheme>(`/api/Theme/${id}`, theme);
    },

    async deleteTheme(id: number): Promise<ApiResponse<void>> {
      return apiClient.delete<void>(`/api/Theme/${id}`);
    },

    async setActiveTheme(id: number, applicationType: string = 'Both'): Promise<ApiResponse<void>> {
      return apiClient.post<void>(`/api/Theme/${id}/activate?applicationType=${encodeURIComponent(applicationType)}`);
    },

    async previewTheme(id: number): Promise<string> {
      // This would need custom implementation if preview endpoint is needed
      // For now, get theme and generate CSS on client
      const response = await this.getThemeById(id);
      return response.data ? 'Preview CSS would go here' : '';
    },
  };
}
// Force rebuild
